import React, { useEffect, useState } from "react";
import { TimelineData } from "../Timeline/Types";
import {
    CountdownTypography,
    GameClockTypography,
    GameInfoContainer,
    GameScore,
    GameStateInfo,
    TeamInfoContainer,
    InProgressAnimation,
    GameInfoFirstRow,
} from "./GameTimelineHeader.styles";
import { useTimelineHeader } from "../Hooks/useTimelineHeader";
import {
    GAME_GENERAL_STATE,
    isMatchOngoingOrFinished,
    useGameGeneralState,
} from "../Hooks/useGameGeneralState";
import { Box, Stack, Typography } from "@mui/material";
import { TeamInfo } from "./TeamInfo";
import { TeamHistoryStepper } from "./TeamHistory/TeamHistoryStepper";
import { getGameTeamHistories, TeamHistoriesType } from "./TeamHistory/Service";
import CountDown from "count-down-react";
import { TeamGoalsType, useGameGoals } from "../Hooks/useGameGoals";
import { AwayTeamSectionType, HomeTeamSectionType } from "./GameState.types";

const GAME_INFO_SECTION_WIDTH_PERCENTAGE = 0.15;
const TEAM_SECTION_WIDTH_PERCENTAGE =
    (1 - GAME_INFO_SECTION_WIDTH_PERCENTAGE) / 2;

export function GameState({
    timeline,
    postId,
}: {
    postId: string;
    timeline: TimelineData;
}) {
    const [teamsHistories, setTeamsHistories] = useState<TeamHistoriesType>();
    useEffect(() => {
        getGameTeamHistories(postId).then(setTeamsHistories);
    }, []);

    return (
        <GameStateInfo>
            <HomeTeam
                teamHomeHistory={teamsHistories?.teamHistories?.teamHomeHistory}
                timeline={timeline}
            />
            <GameInfo timeline={timeline} />
            <AwayTeam
                awayTeamHistory={teamsHistories?.teamHistories?.teamAwayHistory}
                timeline={timeline}
            />
        </GameStateInfo>
    );
}

function HomeTeam({ teamHomeHistory, timeline }: HomeTeamSectionType) {
    const { teamHomeGoals } = useGameGoals(timeline.timeline);
    const gameState = useGameGeneralState(timeline);
    return (
        <TeamInfoContainer
            flex={TEAM_SECTION_WIDTH_PERCENTAGE}
            alignItems={"end"}
        >
            <TeamInfo teamData={timeline.sportEvent.competitors.home} />
            {gameState === GAME_GENERAL_STATE.NOT_STARTED ? (
                <TeamHistoryStepper teamHistory={teamHomeHistory} />
            ) : (
                <PlayersGols teamGoals={teamHomeGoals} />
            )}
        </TeamInfoContainer>
    );
}

function GameInfo({ timeline }: { timeline: TimelineData }) {
    const gameState = useGameGeneralState(timeline);

    return (
        <GameInfoContainer flex={GAME_INFO_SECTION_WIDTH_PERCENTAGE}>
            {!isMatchOngoingOrFinished(gameState) ? (
                <>
                    <GameInfoFirstRow>
                        <GameClock timeline={timeline} />
                    </GameInfoFirstRow>
                    <CountDown
                        date={timeline.sportEvent.startTime}
                        renderer={CountdownRenderer}
                    />
                </>
            ) : (
                <>
                    <GameInfoFirstRow>
                        <GameScore>
                            {timeline.sportEventStatus.teamHomeGoals ?? "-"}:
                            {timeline.sportEventStatus.teamAwayGoals ?? "-"}
                        </GameScore>
                    </GameInfoFirstRow>
                    <GameClock timeline={timeline} />
                </>
            )}
        </GameInfoContainer>
    );
}

function AwayTeam({ awayTeamHistory, timeline }: AwayTeamSectionType) {
    const { teamAwayGoals } = useGameGoals(timeline.timeline);
    const gameState = useGameGeneralState(timeline);
    return (
        <TeamInfoContainer
            flex={TEAM_SECTION_WIDTH_PERCENTAGE}
            alignItems={"start"}
        >
            <TeamInfo
                reversed
                teamData={timeline.sportEvent.competitors.away}
            />
            {gameState === GAME_GENERAL_STATE.NOT_STARTED ? (
                <TeamHistoryStepper reversed teamHistory={awayTeamHistory} />
            ) : (
                <PlayersGols teamGoals={teamAwayGoals} reversed />
            )}
        </TeamInfoContainer>
    );
}

function GameClock({ timeline }: { timeline: TimelineData }) {
    const clock = useTimelineHeader(timeline);
    const gameState = useGameGeneralState(timeline);
    return (
        <Box>
            <GameClockTypography
                inProgress={gameState === GAME_GENERAL_STATE.IN_PROGRESS}
                style={{
                    ...(gameState && { fontSize: "14px", fontWeight: 700 }),
                }}
            >
                {clock}
            </GameClockTypography>

            {gameState === GAME_GENERAL_STATE.IN_PROGRESS && (
                <InProgressAnimation />
            )}
        </Box>
    );
}

function PlayersGols({
    teamGoals,
    reversed = false,
}: {
    teamGoals: TeamGoalsType[];
    reversed?: boolean;
}) {
    return (
        <Box
            style={{
                display: "flex",
                flexDirection: "column",
                color: "var(--community-color-secondary-text)",
                alignItems: reversed ? "start" : "end",
            }}
        >
            {teamGoals.map((goal) => {
                const text = reversed
                    ? `${goal.time} ${goal.playerName}`
                    : `${goal.playerName} ${goal.time}`;

                return (
                    <Typography
                        key={goal.time}
                        fontSize={"14px"}
                        textAlign={reversed ? "left" : "right"}
                    >
                        {text}
                    </Typography>
                );
            })}
        </Box>
    );
}

const formatTwoDigits = (num: number) => String(num).padStart(2, "0");
const CountdownRenderer = ({
    hours,
    minutes,
    seconds,
}: {
    hours: number;
    minutes: number;
    seconds: number;
}) => (
    <Stack justifyContent={"center"} direction={"row"} alignItems={"center"}>
        <CountdownTypography>{formatTwoDigits(hours)}:</CountdownTypography>
        <CountdownTypography>{formatTwoDigits(minutes)}:</CountdownTypography>
        <CountdownTypography>{formatTwoDigits(seconds)}</CountdownTypography>
    </Stack>
);
